import { z } from 'zod';

export const GIT_MANAGE_FEATURE = 'git_manage';
export const githubSettingsSchema = z.object({
  appId: z.number().int().positive(),
  slug: z.string().trim().regex(/^[a-zA-Z0-9-]+$/).max(100),
  clientId: z.string().trim().min(1).max(100),
  clientSecret: z.string().min(1).max(1000).optional(),
  webhookSecret: z.string().min(32).max(1000).optional(),
  privateKeyUuid: z.string().trim().min(1).max(100),
  serverUuid: z.string().trim().min(1).max(100),
}).strict();
export interface GithubSettingsDto {
  configured: boolean;
  appId: number | null;
  slug: string;
  clientId: string;
  privateKeyUuid: string;
  serverUuid: string;
  hasClientSecret: boolean;
  callbackUrl: string | null;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
}
export interface GithubStatusDto {
  configured: boolean;
  authorized: boolean;
  installUrl: string | null;
}
export interface GithubInstallationDto { id: number; account: string; }
export interface GitConnectionDto { id: string; account: string; createdAt: string; status: 'ready' | 'pending' | 'disconnected' | 'provider_disabled'; accessRevokedReason?: GitAccessRevokedReason | null; }
export interface AdminGitConnectionDto extends GitConnectionDto { clientId: string; clientName: string; }
export interface CustomerGitRepositoryDto { id: number; fullName: string; defaultBranch: string; private: boolean; }
export interface CustomerGitBranchDto { name: string; }
export const connectGitInstallationSchema = z.object({ installationId: z.number().int().positive() }).strict();
const directory = z.string().trim().max(255).refine((value) => !value.split('/').includes('..') && !/[\x00-\x1f]/.test(value), 'Use a directory within the repository');
const gitSiteSchema = z.object({
  subscriptionId: z.string().uuid(),
  connectionId: z.string().uuid(),
  repositoryId: z.number().int().positive(),
  branch: z.string().trim().min(1).max(250),
  name: z.string().trim().min(1).max(200),
  buildPack: z.enum(['nixpacks', 'railpack', 'static', 'dockerfile']),
  staticSite: z.boolean().default(false),
  spaFallback: z.boolean().default(false),
  baseDirectory: directory.default('/'),
  publishDirectory: directory.default('/'),
  installCommand: z.string().trim().max(1000).optional(),
  buildCommand: z.string().trim().max(1000).optional(),
  startCommand: z.string().trim().max(1000).optional(),
  portsExposes: z.string().regex(/^\d{1,5}$/).refine((n) => Number(n) > 0 && Number(n) <= 65535).default('3000'),
}).strict();
function validateStaticBuild(input: z.infer<typeof gitSiteSchema> | z.infer<typeof gitSettingsSchema>, ctx: z.RefinementCtx) {
  if (input.staticSite && input.buildPack === 'dockerfile') ctx.addIssue({ code: 'custom', path: ['staticSite'], message: 'Dockerfile websites configure their own web server' });
  if (input.spaFallback && !input.staticSite && input.buildPack !== 'static') ctx.addIssue({ code: 'custom', path: ['spaFallback'], message: 'SPA fallback requires static website serving' });
  if (input.staticSite && input.portsExposes !== '80') ctx.addIssue({ code: 'custom', path: ['portsExposes'], message: 'Static website serving uses port 80' });
  if (input.staticSite && input.startCommand) ctx.addIssue({ code: 'custom', path: ['startCommand'], message: 'Static websites use the built-in web server; clear the start command' });
}
export const createGitSiteSchema = gitSiteSchema.superRefine(validateStaticBuild);
export type CreateGitSiteInput = z.infer<typeof createGitSiteSchema>;

const gitSettingsSchema = gitSiteSchema.omit({ subscriptionId: true, connectionId: true, repositoryId: true, name: true }).extend({
  deploymentMode: z.enum(['manual', 'automatic', 'disabled']).default('manual'),
});
export const updateGitSiteSchema = gitSettingsSchema.superRefine(validateStaticBuild);
export type UpdateGitSiteInput = z.infer<typeof updateGitSiteSchema>;
export interface CustomerGitSiteDto {
  id: string;
  name: string;
  repository: string;
  connectionId: string;
  repositoryId: number;
  settings: UpdateGitSiteInput;
  settingsPending: boolean;
  automaticDeployments: GitAutoDeploymentDto[];
  automaticDeploymentReviewRequired: boolean;
}

export interface GitAutoDeploymentDto {
  id: string;
  branch: string;
  commit: string;
  status: 'queued' | 'requesting' | 'submitted' | 'skipped' | 'uncertain' | 'reviewed';
  summary: string;
  createdAt: string;
}

export type GitAccessRevokedReason = 'suspended' | 'uninstalled' | 'repositories_removed';
export const GIT_ACCESS_REVOKED_LABELS: Record<GitAccessRevokedReason, string> = {
  suspended: 'GitHub suspended this installation. Restore access in GitHub, then reconnect.',
  uninstalled: 'The GitHub App was uninstalled. Reinstall it, then ask your provider to migrate affected websites to the new connection.',
  repositories_removed: 'The installation lost repository access. Review the selected repositories in GitHub, then reconnect.',
};
export interface GithubReadinessCheck {
  id: 'panel_url' | 'credentials' | 'webhook' | 'app_identity' | 'app_permissions' | 'push_event' | 'server' | 'live_acceptance';
  status: 'passed' | 'attention' | 'unverified';
  summary: string;
}
export interface GithubReadinessDto { checkedAt: string; checks: GithubReadinessCheck[]; }
